import type { HeaderContent } from '@/blocks/domain/types/BlockContent/HeaderContent';
import { moveElement, moveSection, sectionTreeOf, type TreeSection } from './sectionTree';

/**
 * Where a drag in the Sections panel lands (LLAMS-610 DB-12). The panel is ONE multi-container
 * sortable, so a drop is described by two ids: what was dragged and what it ended over.
 *
 *   `section:<id>`     a visible section row (a drag handle target for whole sections)
 *   `col:<id>:<n>`     a column's own drop zone (the only target an EMPTY column has)
 *   anything else      an element id (loose element or element inside a column)
 */
export const sectionDragId = (sectionId: string): string => `section:${sectionId}`;
export const columnDropId = (sectionId: string, column: number): string => `col:${sectionId}:${column}`;

/** A column on the CANVAS (the live page), a drop zone for an Add-panel tile; distinct from the panel's `col:` ids. */
export const canvasColumnDropId = (sectionId: string, column: number): string => `canvas:${columnDropId(sectionId, column)}`;

export function parseSectionDragId(id: string): string | null {
  return id.startsWith('section:') ? id.slice('section:'.length) : null;
}

export interface ColumnDrop {
  sectionId: string;
  column: number;
}

export function parseColumnDropId(id: string): ColumnDrop | null {
  if (!id.startsWith('col:')) return null;
  const at = id.lastIndexOf(':');
  const column = Number(id.slice(at + 1));
  const sectionId = id.slice('col:'.length, at);
  return Number.isInteger(column) && sectionId ? { sectionId, column } : null;
}

/** The column behind either kind of tile drop zone: a panel chip (`col:`) or a canvas column (`canvas:col:`). */
export function parseTileDropId(id: string): ColumnDrop | null {
  return parseColumnDropId(id.startsWith('canvas:') ? id.slice('canvas:'.length) : id);
}

function sectionOfElement(tree: TreeSection[], fieldId: string): TreeSection | undefined {
  return tree.find((s) => s.columns.some((c) => c.some((f) => f.id === fieldId)));
}

/** Top-level index of `over` (a section or a loose element) among the sections left after the move. */
function topIndexOf(tree: TreeSection[], over: TreeSection, movingSectionId: string | null, after: boolean): number {
  const rest = tree.filter((s) => s.id !== movingSectionId);
  const at = rest.findIndex((s) => s.id === over.id);
  return at < 0 ? rest.length : at + (after ? 1 : 0);
}

function dropSection(content: HeaderContent, tree: TreeSection[], sectionId: string, overId: string, after: boolean): HeaderContent {
  const overSection =
    tree.find((s) => s.id === parseSectionDragId(overId)) ??
    tree.find((s) => s.id === parseColumnDropId(overId)?.sectionId) ??
    sectionOfElement(tree, overId);
  if (!overSection || overSection.id === sectionId) return content;
  return moveSection(content, sectionId, topIndexOf(tree, overSection, sectionId, after));
}

function dropElement(content: HeaderContent, tree: TreeSection[], fieldId: string, overId: string, after: boolean): HeaderContent {
  if (overId === fieldId) return content;
  const home = sectionOfElement(tree, fieldId);
  const homeAlone = home?.implicit ? home.id : null;

  const column = parseColumnDropId(overId);
  if (column) {
    const section = tree.find((s) => s.id === column.sectionId);
    const list = (section?.columns[column.column] ?? []).filter((f) => f.id !== fieldId);
    return moveElement(content, fieldId, { kind: 'column', ...column, index: list.length });
  }

  const sectionId = parseSectionDragId(overId);
  const overSection = sectionId ? tree.find((s) => s.id === sectionId) : sectionOfElement(tree, overId);
  if (!overSection) return content;

  // A section HEADER is a drop target for "into this section": the element goes to the top of its first
  // column, never out to the top level (that would mint a new implicit section - the DND-05 failure).
  if (sectionId && !overSection.implicit) {
    return moveElement(content, fieldId, { kind: 'column', sectionId: overSection.id, column: 0, index: 0 });
  }

  if (overSection.implicit) {
    return moveElement(content, fieldId, { kind: 'top', index: topIndexOf(tree, overSection, homeAlone, after) });
  }
  const columnIndex = overSection.columns.findIndex((c) => c.some((f) => f.id === overId));
  const list = (overSection.columns[columnIndex] ?? []).filter((f) => f.id !== fieldId);
  const at = list.findIndex((f) => f.id === overId);
  return moveElement(content, fieldId, {
    kind: 'column',
    sectionId: overSection.id,
    column: columnIndex,
    index: at + (after ? 1 : 0)
  });
}

/** Apply one finished drag. Returns `content` itself (same reference) when nothing changes. */
export function applyTreeDrop(content: HeaderContent, activeId: string, overId: string, after: boolean): HeaderContent {
  const tree = sectionTreeOf(content);
  const sectionId = parseSectionDragId(activeId);
  if (sectionId) return dropSection(content, tree, sectionId, overId, after);
  return dropElement(content, tree, activeId, overId, after);
}
